const TERMINAL_CANCEL_STATUSES = new Set(['completed', 'on_site', 'cancelled']);

const URGENT_URGENCY_VALUES = new Set([
  'within-hour',
  'today',
  'urgent',
  "dans l'heure",
  'aujourd\'hui',
  'prioritaire',
  'sous 2h',
]);

function normalizeTimePart(scheduledTimeStart) {
  if (!scheduledTimeStart) return '00:00:00';
  const raw = String(scheduledTimeStart).trim();
  if (/^\d{2}:\d{2}:\d{2}/.test(raw)) return raw.slice(0, 8);
  if (/^\d{2}:\d{2}$/.test(raw)) return `${raw}:00`;
  return null;
}

function normalizeDatePart(scheduledDate) {
  if (!scheduledDate) return null;
  if (scheduledDate instanceof Date && !Number.isNaN(scheduledDate.getTime())) {
    return scheduledDate.toISOString().slice(0, 10);
  }
  const raw = String(scheduledDate).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  return null;
}

export function getAppointmentStartDate(scheduledDate, scheduledTimeStart) {
  const datePart = normalizeDatePart(scheduledDate);
  const timePart = normalizeTimePart(scheduledTimeStart);
  if (!datePart || !timePart) return null;

  const parsed = new Date(`${datePart}T${timePart}`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function isAppointmentInThePast(scheduledDate, scheduledTimeStart, now = new Date()) {
  const start = getAppointmentStartDate(scheduledDate, scheduledTimeStart);
  if (!start) return true;
  return start.getTime() < now.getTime();
}

export function canShowCancelAction({
  status,
  scheduledDate,
  scheduledTimeStart,
  now = new Date(),
} = {}) {
  if (TERMINAL_CANCEL_STATUSES.has(status)) return false;
  if (isAppointmentInThePast(scheduledDate, scheduledTimeStart, now)) return false;
  return true;
}

export function formatCancellationCountdown(deadline, now = new Date()) {
  if (!(deadline instanceof Date) || Number.isNaN(deadline.getTime())) return null;
  if (now >= deadline) return null;

  const diffMs = deadline.getTime() - now.getTime();
  const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  if (diffHrs > 0) {
    return `${diffHrs}h${diffMins > 0 ? ` ${diffMins}min` : ''}`;
  }
  if (diffMins > 0) {
    return `${diffMins} minutes`;
  }
  return null;
}

export function formatFrenchDate(dateValue) {
  const datePart = normalizeDatePart(dateValue);
  if (!datePart) return '';
  const [year, month, day] = datePart.split('-');
  return `${day}/${month}/${year}`;
}

/** Date + heure de demande : JJ/MM/AAAA HH:MM */
export function formatFrenchDateTime(dateValue) {
  if (!dateValue) return '';
  const parsed = dateValue instanceof Date ? dateValue : new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) {
    return formatFrenchDate(dateValue);
  }
  const day = String(parsed.getDate()).padStart(2, '0');
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const year = parsed.getFullYear();
  const hours = String(parsed.getHours()).padStart(2, '0');
  const minutes = String(parsed.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
}

export function isQuoteAppointment(appointment) {
  if (appointment?.isQuote ?? appointment?.is_quote) {
    return true;
  }

  const paymentMethod = `${appointment?.paymentMethod || appointment?.payment_method || ''}`.toLowerCase();
  return paymentMethod === 'sur_devis' || paymentMethod === 'sur devis';
}

/** Affichage onglet Devis (y compris devis clôturé après réservation). */
export function isQuoteListItem(appointment) {
  return isQuoteAppointment(appointment) || Boolean(appointment?.hasQuote);
}

export function getAppointmentDisplayDate(appointment, listType = null) {
  // Sous Interventions : toujours la date d'intervention
  if (listType === 'interventions') {
    return appointment?.scheduledDate || appointment?.scheduled_date
      || appointment?.createdAt || appointment?.created_at;
  }
  // Sous Devis : date de demande / création
  if (listType === 'devis' || isQuoteAppointment(appointment) || appointment?.hasQuote) {
    return (
      appointment?.createdAt
      || appointment?.created_at
      || appointment?.scheduledDate
      || appointment?.scheduled_date
    );
  }
  return appointment?.scheduledDate || appointment?.scheduled_date;
}

/**
 * Labels des pastilles selon l'onglet parent (pas selon l'historique devis).
 * - interventions → « Prestation du … »
 * - devis → « Devis du … »
 */
export function getAppointmentTabLabel(appointment, listType = null) {
  const asQuote = listType === 'devis'
    ? true
    : listType === 'interventions'
      ? false
      : isQuoteListItem(appointment);

  const dateLabel = formatFrenchDate(getAppointmentDisplayDate(appointment, listType));
  if (!dateLabel) {
    return asQuote ? 'Devis' : 'Prestation';
  }
  return asQuote
    ? `Devis du ${dateLabel}`
    : `Demande du ${dateLabel}`;
}

/** Clé de tri alignée sur dateLabel (YYYY-MM-DD + heure éventuelle). */
export function getAppointmentSortKey(appointment) {
  const datePart = normalizeDatePart(getAppointmentDisplayDate(appointment));
  if (!datePart) return '0000-00-00T00:00:00';

  if (isQuoteAppointment(appointment) || appointment?.hasQuote) {
    const created = appointment?.createdAt || appointment?.created_at;
    if (created) {
      const parsed = new Date(created);
      if (!Number.isNaN(parsed.getTime())) return parsed.toISOString();
    }
    return `${datePart}T00:00:00`;
  }

  const timePart = normalizeTimePart(
    appointment?.scheduledTimeStart || appointment?.scheduled_time_start
  ) || '00:00:00';
  return `${datePart}T${timePart}`;
}

export function sortAppointmentsByDisplayDate(appointments = []) {
  return [...appointments].sort((a, b) =>
    getAppointmentSortKey(b).localeCompare(getAppointmentSortKey(a))
  );
}

/** Tri interventions par date/heure d’intervention (plus récente d’abord). */
export function sortAppointmentsByScheduledDate(appointments = []) {
  return [...appointments].sort((a, b) => {
    const aDate = normalizeDatePart(a?.scheduledDate || a?.scheduled_date) || '0000-00-00';
    const bDate = normalizeDatePart(b?.scheduledDate || b?.scheduled_date) || '0000-00-00';
    const aTime =
      normalizeTimePart(a?.scheduledTimeStart || a?.scheduled_time_start) || '00:00:00';
    const bTime =
      normalizeTimePart(b?.scheduledTimeStart || b?.scheduled_time_start) || '00:00:00';
    return `${bDate}T${bTime}`.localeCompare(`${aDate}T${aTime}`);
  });
}

export function getQuoteProblemName(appointment) {
  return (
    appointment?.problemName
    || appointment?.service?.problem?.name
    || appointment?.Service?.problem?.name
    || null
  );
}

export function getQuoteServiceName(appointment) {
  return (
    appointment?.serviceType
    || appointment?.service_type
    || appointment?.service?.name
    || appointment?.Service?.name
    || null
  );
}

export function getQuoteSubject(appointment) {
  const problem = getQuoteProblemName(appointment);
  const service = getQuoteServiceName(appointment);

  if (problem?.trim() && service?.trim() && problem.trim() !== service.trim()) {
    return `${problem.trim()} — ${service.trim()}`;
  }

  return problem?.trim() || service?.trim() || appointment?.notes?.trim() || 'Demande de devis';
}

export function formatScheduledTimeDisplay(scheduledTimeStart) {
  const timePart = normalizeTimePart(scheduledTimeStart);
  if (!timePart) return '';
  return timePart.slice(0, 5);
}

export function isUrgentAppointmentUrgency(urgency) {
  if (urgency == null || urgency === '') return false;
  const normalized = String(urgency).trim().toLowerCase();
  return URGENT_URGENCY_VALUES.has(normalized);
}

export function getMinRescheduleDate(now = new Date()) {
  const min = new Date(now);
  min.setDate(min.getDate() + 1);
  min.setHours(0, 0, 0, 0);
  return min;
}

export function getRescheduleBlockedReason({
  urgency,
  scheduledDate,
  scheduledTimeStart,
  status,
  now = new Date(),
} = {}) {
  if (TERMINAL_CANCEL_STATUSES.has(status)) {
    return 'Cette intervention ne peut plus être reprogrammée.';
  }
  if (isUrgentAppointmentUrgency(urgency)) {
    return 'Les prestations réservées en créneau urgent ne sont pas reprogrammables.';
  }
  if (isAppointmentInThePast(scheduledDate, scheduledTimeStart, now)) {
    return 'Cette intervention ne peut plus être reprogrammée (date dépassée).';
  }
  return null;
}

export function canRescheduleAppointment(params = {}) {
  return getRescheduleBlockedReason(params) === null;
}

export function getCancellationDeadline(scheduledDate, scheduledTimeStart) {
  const start = getAppointmentStartDate(scheduledDate, scheduledTimeStart);
  if (!start) return null;
  return new Date(start.getTime() - 60 * 60 * 1000);
}

/**
 * Frais d'annulation selon le délai avant l'intervention (CGV) :
 * ≥ 72 h → 0 € | [48 h, 72 h) → 30 € | [24 h, 48 h) → 40 € | < 24 h → 50 €
 */
export function getCancellationFeeEuros(scheduledDate, scheduledTimeStart, now = new Date()) {
  const start = getAppointmentStartDate(scheduledDate, scheduledTimeStart);
  if (!start) return 50;

  const hoursUntil = (start.getTime() - now.getTime()) / (1000 * 60 * 60);
  if (hoursUntil >= 72) return 0;
  if (hoursUntil >= 48) return 30;
  if (hoursUntil >= 24) return 40;
  return 50;
}

export function formatCancellationFeeLabel(amountEuros) {
  const amount = Number(amountEuros);
  if (!Number.isFinite(amount)) return '—';
  return `${amount}\u00a0€`;
}
